import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  useTheme,
  Tab,
  Tabs,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon, 
  Edit as EditIcon,
  CalendarToday as CalendarIcon,
  AccessTime as AccessTimeIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import { format, addDays, startOfDay } from 'date-fns';

import athenaService from '../services/athenaService';
import AvailabilityService from '../services/availabilityService';

// Component to manage provider availability
const AvailabilityManager = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  
  // Data
  const [departments, setDepartments] = useState([]);
  const [providers, setProviders] = useState([]);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [existingAvailability, setExistingAvailability] = useState({});
  
  // Single Day Form
  const [singleDayForm, setSingleDayForm] = useState({
    departmentId: '',
    providerId: '',
    appointmentTypeId: '',
    date: new Date(),
    timeSlots: [{ startTime: '09:00', endTime: '09:30' }]
  });
  
  // Recurring Form
  const [recurringForm, setRecurringForm] = useState({
    departmentId: '',
    providerId: '',
    appointmentTypeId: '',
    startDate: new Date(),
    endDate: addDays(new Date(), 30),
    days: [1, 2, 3, 4, 5], // Mon, Tue, Wed, Thu, Fri
    times: ['09:00', '10:00', '11:00', '13:00', '14:00', '15:00', '16:00']
  });
  
  // Notification state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Load departments on mount
  useEffect(() => {
    const loadDepartments = async () => {
      setLoading(true);
      try {
        const departmentsData = await athenaService.getDepartments();
        
        // Filter out departments with empty providerlist
        const filteredDepts = departmentsData.filter(dept => 
          dept.providerlist && dept.providerlist.length > 0
        );
        
        console.log('Availability: Departments with providers:', filteredDepts);
        setDepartments(filteredDepts);
      } catch (error) {
        console.error('Error loading departments:', error);
        showSnackbar('Failed to load departments', 'error');
      } finally {
        setLoading(false);
      }
    };
    
    loadDepartments();
  }, []);
  
  // Load providers when department changes
  useEffect(() => {
    const loadProviders = async () => {
      const departmentId = tabIndex === 0 
        ? singleDayForm.departmentId 
        : recurringForm.departmentId;
        
      if (!departmentId) return;
      
      setLoading(true);
      try {
        // Find the selected department to get its providerlist
        const department = departments.find(d => d.departmentid === departmentId);
        
        // Get all providers for the department
        const providersData = await athenaService.getProviders(departmentId);
        
        // If department has a providerlist, filter providers to only include those in the list
        if (department && department.providerlist && department.providerlist.length > 0) {
          const filteredProviders = providersData.filter(p => 
            department.providerlist.includes(p.providerid.toString())
          );
          console.log('Filtered providers for department:', filteredProviders);
          setProviders(filteredProviders || []);
        } else {
          // If no providerlist is available, use all providers
          setProviders(providersData || []);
        }
      } catch (error) {
        console.error('Error loading providers:', error);
        showSnackbar('Failed to load providers', 'error');
      } finally {
        setLoading(false);
      }
    };
    
    loadProviders();
  }, [singleDayForm.departmentId, recurringForm.departmentId, tabIndex]);
  
  // Load appointment types when provider changes
  useEffect(() => {
    const loadAppointmentTypes = async () => {
      const departmentId = tabIndex === 0 
        ? singleDayForm.departmentId 
        : recurringForm.departmentId;
      const providerId = tabIndex === 0 
        ? singleDayForm.providerId 
        : recurringForm.providerId;
        
      if (!departmentId || !providerId) return;
      
      setLoading(true);
      try {
        const typesData = await athenaService.getAppointmentTypes(departmentId, providerId);
        setAppointmentTypes(typesData);
      } catch (error) {
        console.error('Error loading appointment types:', error);
        showSnackbar('Failed to load appointment types', 'error');
      } finally {
        setLoading(false);
      }
    };
    
    loadAppointmentTypes();
  }, [
    singleDayForm.departmentId, 
    singleDayForm.providerId, 
    recurringForm.departmentId, 
    recurringForm.providerId, 
    tabIndex
  ]);
  
  // Load existing availability
  useEffect(() => {
    const loadExistingAvailability = async () => {
      const departmentId = tabIndex === 0 
        ? singleDayForm.departmentId 
        : recurringForm.departmentId;
      const providerId = tabIndex === 0 
        ? singleDayForm.providerId 
        : recurringForm.providerId;
      
      if (!departmentId || !providerId) return;
      
      setLoading(true);
      try {
        // Date range for fetching availability
        const startDate = tabIndex === 0 
          ? format(singleDayForm.date, 'MM/dd/yyyy') 
          : format(recurringForm.startDate, 'MM/dd/yyyy');
        const endDate = tabIndex === 0 
          ? format(singleDayForm.date, 'MM/dd/yyyy') 
          : format(recurringForm.endDate, 'MM/dd/yyyy');
        
        // Get the appointmentTypeId if selected
        const appointmentTypeId = tabIndex === 0 
          ? singleDayForm.appointmentTypeId 
          : recurringForm.appointmentTypeId;

        // Get available slots from athenaService
        const availability = await AvailabilityService.getAvailableSlotsFromAthena(
          departmentId,
          appointmentTypeId, // Include appointmentTypeId when selected
          providerId,
          startDate,
          endDate
        );
        
        setExistingAvailability(availability);
      } catch (error) {
        console.error('Error loading existing availability:', error);
        // Fallback to local availability
        const availability = AvailabilityService.getAvailabilities();
        setExistingAvailability(availability);
      } finally {
        setLoading(false);
      }
    };
    
    loadExistingAvailability();
  }, [
    singleDayForm.departmentId, 
    singleDayForm.providerId, 
    singleDayForm.date,
    recurringForm.departmentId, 
    recurringForm.providerId,
    recurringForm.startDate,
    recurringForm.endDate,
    tabIndex
  ]);
  
  // Handle tab change
  const handleTabChange = (event, newIndex) => {
    setTabIndex(newIndex);
  };
  
  // Show snackbar notification
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };
  
  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };
  
  // Single Day Form Handlers
  const handleSingleDayFormChange = (field, value) => {
    setSingleDayForm(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const handleTimeSlotChange = (index, field, value) => {
    setSingleDayForm(prev => {
      const newTimeSlots = [...prev.timeSlots];
      newTimeSlots[index] = {
        ...newTimeSlots[index],
        [field]: value
      };
      return {
        ...prev,
        timeSlots: newTimeSlots
      };
    });
  };
  
  const addTimeSlot = () => {
    setSingleDayForm(prev => ({
      ...prev,
      timeSlots: [...prev.timeSlots, { startTime: '09:00', endTime: '09:30' }]
    }));
  };
  
  const removeTimeSlot = (index) => {
    setSingleDayForm(prev => {
      const newTimeSlots = [...prev.timeSlots];
      newTimeSlots.splice(index, 1);
      return {
        ...prev,
        timeSlots: newTimeSlots
      };
    });
  };
  
  // Recurring Form Handlers
  const handleRecurringFormChange = (field, value) => {
    setRecurringForm(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const handleDayToggle = (day) => {
    setRecurringForm(prev => {
      const newDays = [...prev.days];
      const index = newDays.indexOf(day);
      if (index > -1) {
        newDays.splice(index, 1);
      } else {
        newDays.push(day);
      }
      return {
        ...prev,
        days: newDays
      };
    });
  };
  
  const handleTimeToggle = (time) => {
    setRecurringForm(prev => {
      const newTimes = [...prev.times];
      const index = newTimes.indexOf(time);
      if (index > -1) {
        newTimes.splice(index, 1);
      } else {
        newTimes.push(time);
        newTimes.sort(); // Keep times sorted
      }
      return {
        ...prev,
        times: newTimes
      };
    });
  };
  
  // Save availability
  const handleSaveSingleDayAvailability = async () => {
    setLoading(true);
    try {
      // Validate form
      if (!singleDayForm.departmentId || !singleDayForm.providerId || 
          !singleDayForm.appointmentTypeId || !singleDayForm.date) {
        showSnackbar('Please fill in all required fields', 'error');
        return;
      }
      
      if (singleDayForm.timeSlots.length === 0) {
        showSnackbar('Please add at least one time slot', 'error');
        return;
      }
      
      // Save to Athena
      const result = await AvailabilityService.createAthenaAvailability(
        singleDayForm.departmentId,
        singleDayForm.providerId,
        singleDayForm.appointmentTypeId,
        singleDayForm.date,
        singleDayForm.timeSlots
      );
      
      // Check the response for appointment IDs
      const appointmentIds = result.appointmentids || {};
      const createdCount = Object.keys(appointmentIds).length;
      
      showSnackbar(`Successfully created ${createdCount} availability slot(s)`);
      
      // Refresh existing availability
      const availability = await AvailabilityService.getAvailableSlotsFromAthena(
        singleDayForm.departmentId,
        null,
        singleDayForm.providerId,
        format(singleDayForm.date, 'MM/dd/yyyy'),
        format(singleDayForm.date, 'MM/dd/yyyy')
      );
      
      setExistingAvailability(availability);
    } catch (error) {
      console.error('Error saving single day availability:', error);
      showSnackbar('Failed to save availability: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSaveRecurringAvailability = async () => {
    setLoading(true);
    try {
      // Validate form
      if (!recurringForm.departmentId || !recurringForm.providerId || 
          !recurringForm.appointmentTypeId || !recurringForm.startDate || 
          !recurringForm.endDate) {
        showSnackbar('Please fill in all required fields', 'error');
        return;
      }
      
      if (recurringForm.days.length === 0) {
        showSnackbar('Please select at least one day of the week', 'error');
        return;
      }
      
      if (recurringForm.times.length === 0) {
        showSnackbar('Please select at least one time slot', 'error');
        return;
      }
      
      // Create pattern object
      const pattern = {
        days: recurringForm.days,
        times: recurringForm.times
      };
      
      // Save to Athena
      const results = await AvailabilityService.createRecurringAthenaAvailability(
        recurringForm.departmentId,
        recurringForm.providerId,
        recurringForm.appointmentTypeId,
        pattern,
        recurringForm.startDate,
        recurringForm.endDate
      );
      
      // Calculate total slots created across all days
      let totalSlotsCreated = 0;
      for (const result of results) {
        const appointmentIds = result.appointmentids || {};
        totalSlotsCreated += Object.keys(appointmentIds).length;
      }
      
      showSnackbar(`Successfully created recurring availability with ${totalSlotsCreated} total slots across ${results.length} days`);
      
      // Refresh existing availability
      const availability = await AvailabilityService.getAvailableSlotsFromAthena(
        recurringForm.departmentId,
        null,
        recurringForm.providerId,
        format(recurringForm.startDate, 'MM/dd/yyyy'),
        format(recurringForm.endDate, 'MM/dd/yyyy')
      );
      
      setExistingAvailability(availability);
    } catch (error) {
      console.error('Error saving recurring availability:', error);
      showSnackbar('Failed to save recurring availability: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Render time slots for single day form
  const renderTimeSlots = () => {
    return singleDayForm.timeSlots.map((slot, index) => (
      <Grid container spacing={2} key={index} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={5}>
          <TextField
            label="Start Time (HH:MM)"
            value={slot.startTime}
            onChange={(e) => handleTimeSlotChange(index, 'startTime', e.target.value)}
            fullWidth
            size="small"
            inputProps={{ pattern: "[0-9]{2}:[0-9]{2}" }}
            placeholder="09:00"
            helperText="24-hour format (9am = 09:00)"
          />
        </Grid>
        <Grid item xs={5}>
          <TextField
            label="End Time (HH:MM)"
            value={slot.endTime}
            onChange={(e) => handleTimeSlotChange(index, 'endTime', e.target.value)}
            fullWidth
            size="small"
            inputProps={{ pattern: "[0-9]{2}:[0-9]{2}" }}
            placeholder="09:30"
            helperText="Only start time is used by Athena API"
          />
        </Grid>
        <Grid item xs={2}>
          <IconButton 
            color="error" 
            onClick={() => removeTimeSlot(index)}
            disabled={singleDayForm.timeSlots.length <= 1}
          >
            <DeleteIcon />
          </IconButton>
        </Grid>
      </Grid>
    ));
  };
  
  // Render days of week checkboxes
  const renderDaysOfWeek = () => {
    const days = [
      { value: 0, label: 'Sun' },
      { value: 1, label: 'Mon' },
      { value: 2, label: 'Tue' },
      { value: 3, label: 'Wed' },
      { value: 4, label: 'Thu' },
      { value: 5, label: 'Fri' },
      { value: 6, label: 'Sat' }
    ];
    
    return (
      <FormGroup row>
        {days.map((day) => (
          <FormControlLabel
            key={day.value}
            control={
              <Checkbox
                checked={recurringForm.days.includes(day.value)}
                onChange={() => handleDayToggle(day.value)}
                name={day.label}
              />
            }
            label={day.label}
          />
        ))}
      </FormGroup>
    );
  };
  
  // Render time options for recurring form
  const renderTimeOptions = () => {
    const times = [];
    for (let hour = 9; hour <= 17; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const formattedHour = hour.toString().padStart(2, '0');
        const formattedMinute = minute.toString().padStart(2, '0');
        times.push(`${formattedHour}:${formattedMinute}`);
      }
    }
    
    return (
      <Grid container spacing={1}>
        {times.map((time) => (
          <Grid item key={time}>
            <Button
              variant={recurringForm.times.includes(time) ? "contained" : "outlined"}
              size="small"
              onClick={() => handleTimeToggle(time)}
              sx={{ minWidth: '80px' }}
            >
              {time}
            </Button>
          </Grid>
        ))}
      </Grid>
    );
  };
  
  // Render existing availability
  const renderExistingAvailability = () => {
    const dateKeys = Object.keys(existingAvailability);
    
    if (dateKeys.length === 0) {
      return (
        <Box sx={{ mt: 2, p: 2, textAlign: 'center' }}>
          <Typography color="textSecondary">
            No availability found for the selected criteria.
          </Typography>
        </Box>
      );
    }
    
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Existing Availability
        </Typography>
        <Grid container spacing={2}>
          {dateKeys.map((dateKey) => (
            <Grid item xs={12} md={6} lg={4} key={dateKey}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    {dateKey}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Grid container spacing={1}>
                    {existingAvailability[dateKey].map((time) => (
                      <Grid item key={time}>
                        <Chip 
                          icon={<AccessTimeIcon />} 
                          label={time} 
                          color="primary" 
                          variant="outlined" 
                          size="small"
                        />
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };
  
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ height: 'calc(100vh - 20px)', overflowY: 'auto', p: 0 }}>
        <Paper 
          elevation={0} 
          sx={{ 
            width: '100%', 
            p: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: 'white',
            borderRadius: 0,
            borderBottom: `1px solid #e0e0e0`,
            mb: 3
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight="bold">Provider Availability Manager</Typography>
          </Box>
        </Paper>
          
        <Box sx={{ px: 3 }}>
          <Tabs value={tabIndex} onChange={handleTabChange} sx={{ mb: 3 }}>
            <Tab label="Single Day" />
            <Tab label="Recurring Pattern" />
          </Tabs>
          
          {/* Single Day Tab */}
          {tabIndex === 0 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Create Single Day Availability
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Department</InputLabel>
                    <Select
                      value={singleDayForm.departmentId}
                      onChange={(e) => handleSingleDayFormChange('departmentId', e.target.value)}
                      label="Department"
                    >
                      {departments.map((dept) => (
                        <MenuItem key={dept.departmentid} value={dept.departmentid}>
                          {dept.name} [{dept.city}]
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth disabled={!singleDayForm.departmentId}>
                    <InputLabel>Provider</InputLabel>
                    <Select
                      value={singleDayForm.providerId}
                      onChange={(e) => handleSingleDayFormChange('providerId', e.target.value)}
                      label="Provider"
                    >
                      {providers.map((provider) => (
                        <MenuItem key={provider.providerid} value={provider.providerid}>
                          {provider.displayname || `${provider.firstname} ${provider.lastname}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth disabled={!singleDayForm.providerId}>
                    <InputLabel>Appointment Type</InputLabel>
                    <Select
                      value={singleDayForm.appointmentTypeId}
                      onChange={(e) => handleSingleDayFormChange('appointmentTypeId', e.target.value)}
                      label="Appointment Type"
                    >
                      {appointmentTypes.map((type) => (
                        <MenuItem key={type.appointmenttypeid} value={type.appointmenttypeid}>
                          {type.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="Date"
                    value={singleDayForm.date}
                    onChange={(newDate) => handleSingleDayFormChange('date', newDate)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Time Slots
                </Typography>
                
                {renderTimeSlots()}
                
                <Button 
                  startIcon={<AddIcon />} 
                  onClick={addTimeSlot}
                  sx={{ mt: 1 }}
                >
                  Add Time Slot
                </Button>
              </Box>
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveSingleDayAvailability}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Save Availability'}
                </Button>
              </Box>
            </Paper>
          )}
          
          {/* Recurring Tab */}
          {tabIndex === 1 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Create Recurring Availability
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Department</InputLabel>
                    <Select
                      value={recurringForm.departmentId}
                      onChange={(e) => handleRecurringFormChange('departmentId', e.target.value)}
                      label="Department"
                    >
                      {departments.map((dept) => (
                        <MenuItem key={dept.departmentid} value={dept.departmentid}>
                          {dept.name} [{dept.city}]
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth disabled={!recurringForm.departmentId}>
                    <InputLabel>Provider</InputLabel>
                    <Select
                      value={recurringForm.providerId}
                      onChange={(e) => handleRecurringFormChange('providerId', e.target.value)}
                      label="Provider"
                    >
                      {providers.map((provider) => (
                        <MenuItem key={provider.providerid} value={provider.providerid}>
                          {provider.displayname || `${provider.firstname} ${provider.lastname}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth disabled={!recurringForm.providerId}>
                    <InputLabel>Appointment Type</InputLabel>
                    <Select
                      value={recurringForm.appointmentTypeId}
                      onChange={(e) => handleRecurringFormChange('appointmentTypeId', e.target.value)}
                      label="Appointment Type"
                    >
                      {appointmentTypes.map((type) => (
                        <MenuItem key={type.appointmenttypeid} value={type.appointmenttypeid}>
                          {type.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="Start Date"
                    value={recurringForm.startDate}
                    onChange={(newDate) => handleRecurringFormChange('startDate', newDate)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="End Date"
                    value={recurringForm.endDate}
                    onChange={(newDate) => handleRecurringFormChange('endDate', newDate)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                    minDate={recurringForm.startDate}
                  />
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Days of Week
                </Typography>
                {renderDaysOfWeek()}
              </Box>
              
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Available Times
                </Typography>
                {renderTimeOptions()}
              </Box>
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveRecurringAvailability}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Save Recurring Availability'}
                </Button>
              </Box>
            </Paper>
          )}
          
          {/* Existing Availability Display */}
          {renderExistingAvailability()}
          
          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert 
              onClose={handleCloseSnackbar} 
              severity={snackbar.severity} 
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default AvailabilityManager;
